// This class contains form mapping logic for preneed agreement form
// getFormMappings() would return an array of FoxIt fields with the corresponding value
import { FormMapping } from './FormMapping'
import { useFormFillHelper } from './composables/FormFillHelper'
import { useMerchandise } from '@/mixins/merchandise'
import { Term, type ScenarioOutput } from '@precoa/calculator-sdk'

export default class PreneedContractForm extends FormMapping {
	private readonly enrollmentPaymentDetails: ScenarioOutput | undefined
	constructor(planHolderId: string, enrollmentPaymentDetails?: ScenarioOutput) {
		super(planHolderId)
		this.enrollmentPaymentDetails = enrollmentPaymentDetails
	}

	// Helper to gather payment type in Financial Agreement section
	private getPaymentModeMappings() {
		const term = this.planHolder.insurance.term
		return [
			this.getCheckBoxMapping(
				'paymentToProviderOtherCheck',
				term === Term.SinglePay
			),
			this.getCheckBoxMapping(
				'paymentToProviderMultiPayCheck',
				term !== Term.SinglePay
			),
		]
	}

	private getPreneedPaymentAmountMappings() {
		if (!this.enrollmentPaymentDetails) {
			return []
		}

		const { calculateSectionTotals, setCartItems } = useFormFillHelper()
		const { getPlanCartItems } = useMerchandise()
		const cartItems = getPlanCartItems(this.planHolder.id) ?? []
		setCartItems(cartItems)
		const calculatedTotals = calculateSectionTotals()

		return [
			{
				name: 'gGoodsAndServicesTotal',
				value: calculatedTotals.guaranteedFuneralSubtotal,
			},
			{
				name: 'ngCashAdvanceItemsTotal',
				value: calculatedTotals.nonGuaranteedFuneralSubTotal,
			},
			{
				name: 'singlePayFaceAmount',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.singlePayFaceAmt
				),
			},
			{
				name: 'singlePayPaymentToday',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.singlePayDueTodayAmt
				),
			},
			{
				name: 'financialAgreementDetails',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.singlePayDueTodayAmt
				),
			},
			{
				name: 'multiPayFaceAmount',
				value: this.formatAmount(this.enrollmentPaymentDetails.multiPayFaceAmt),
			},
			{
				name: 'multiPayMonthlyPayment',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.multiPayPeriodicAmt
				),
			},
			{
				name: 'multiPayPaymentToday',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.multiPayDueTodayAmt
				),
			},
			{
				name: 'totalFaceAmount',
				value: this.formatAmount(this.enrollmentPaymentDetails.totalFaceAmt),
			},
			{
				name: 'totalFirstPayment',
				value: this.formatAmount(
					this.enrollmentPaymentDetails.totalDueTodayAmt
				),
			},
		]
	}

	getFormMappings() {
		return [
			...this.getProposedInsuredMapping(),
			...this.getPolicyOwnerMapping(),
			...this.getPolicyOwnerAddressMapping(),
			...this.getProposedInsuredAddressMapping(),
			...this.getFuneralHomeMapping(),
			...this.getRepresentativeSignatureDateMappings(),
			...this.getFuneralHomeRepresentativeMappings(),
			...this.getPaymentModeMappings(),
			...this.getPreneedPaymentAmountMappings(),
		]
	}
}
